import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColorOrString, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import { ClipboardIcon, PrintIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { API, constructApiUrl } from '../../../api/apis.config'
import { getCenter } from '../../../api/centers/get-center'
import { getCenterFinalCostVisibility } from '../../../api/centers/get-center-final-cost-visibility'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import { CenterCosts } from '../../../components/pages/centers/center-costs'
import { centerCostsTableModeMapping } from '../../../components/pages/centers/center-costs/utils'
import { getCenterOlPermissions } from '../../../lib/constants/ol-permissions'
import useAuth from '../../../lib/hooks/use-auth'
import useCurrency from '../../../lib/hooks/use-currency'
import { useDataProps } from '../../../lib/hooks/use-data-props'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import useTranslate from '../../../lib/hooks/use-translate'
import { CenterStatus, centerStatusMapping } from '../../../lib/types/enums/center/center-status'
import { ProductTypeEnum } from '../../../lib/types/enums/product/product-type.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { Center } from '../../../lib/types/models/center/center'
import { CenterProperties } from '../../../lib/types/models/center/properties.center'
import { DataProps } from '../../../lib/types/utils'
import NotFoundPage from '../../common/not-found'

const labelColor: Record<RoleEnum, TailwindColorOrString> = {
  [RoleEnum.Ol]: tailwindColors.purple.DEFAULT,
  [RoleEnum.ADMIN]: tailwindColors.secondary.DEFAULT,
  [RoleEnum.PCR]: tailwindColors.purple.DEFAULT,
  [RoleEnum.STAFF]: tailwindColors.purple.DEFAULT,
  [RoleEnum.CLIENT]: tailwindColors.purple.DEFAULT,
  [RoleEnum.ACCOUNTANT]: tailwindColors.secondary.DEFAULT,
  [RoleEnum.SYSTEM_ADMIN]: tailwindColors.secondary.DEFAULT
}

export function FinalCostOfCenterPage(props: DataProps<Center>) {
  const { t } = useTranslate()
  const { user } = useAuth()
  const { downloadDocuments } = useDownloadDocuments()
  const [isConfirmed, setIsConfirmed] = useState(false)
  const { currency } = useCurrency()

  const { id, data, refetch } = useDataProps(props, (id) => getCenter(id, 'FULL'))

  const { data: finalCostVisibility, isPending: isFinalCostVisibilityPending } = useQuery({
    queryKey: ['final-cost-visibility', id],
    queryFn: () => getCenterFinalCostVisibility(id)
  })

  const mode = centerCostsTableModeMapping({
    role: user?.role,
    status: data?.finalCostsStatus,
    costType: 'final-cost'
  })

  const TextColor =
    user?.role === RoleEnum.Ol ? tailwindColors.purple.DEFAULT : tailwindColors.secondary.DEFAULT

  if (!data || !user || isFinalCostVisibilityPending) return <LoadingScreen />
  if (!finalCostVisibility) return <NotFoundPage />

  const { paymentConfirmationCheckedAt, finalCostsStatus } = data

  const productTypes = CenterProperties.getCenterProductTypes(data)
  const olPermissions = getCenterOlPermissions({
    type: data?.ol?.type,
    role: user.role,
    productTypes
  })

  return (
    <div className={'flex flex-col gap-4'}>
      <div className={'h-2'} />

      <div className={'flex flex-row gap-2 items-center justify-between'}>
        <div className={'flex flex-row gap-2 items-center'}>
          <Typography variant={'sub-heading'}>{t('center.final.costs.title')}</Typography>

          <Badge
            text={t(centerStatusMapping[finalCostsStatus].label)}
            variant={centerStatusMapping[finalCostsStatus].variant}
          />
        </div>

        {user.role === RoleEnum.ADMIN && (
          <div className={'flex flex-row gap-2 items-center'}>
            <Button
              variant={'ghost'}
              icon={PrintIcon}
              onClick={() =>
                downloadDocuments({
                  endpoint: constructApiUrl(API.centers.printCenterFinalCosts.url, { id })
                })
              }
              label={t('center.final.costs.print.final.costs')}
            />
          </div>
        )}
      </div>

      <Box className={'bg-white-hover rounded-xl p-3'}>
        <div className={'flex flex-row gap-2 items-start'}>
          <div
            className={cn(
              'bg-purple-light rounded-full min-w-8 min-h-8 flex items-center justify-center',
              { 'bg-secondary-light': user.role === RoleEnum.ADMIN }
            )}
          >
            <ClipboardIcon color={TextColor} />
          </div>

          <div className={'flex flex-col gap-2 pt-1'}>
            <Typography variant={'text'} color={TextColor}>
              {t('center.final.costs.instructions.title')}
            </Typography>
            <Typography variant={'secondary-body'} color={tailwindColors.grey.lighter}>
              {t('center.final.costs.instructions.number.of.students.and.leaders')}
            </Typography>
            <Typography variant={'secondary-body'} color={tailwindColors.grey.lighter}>
              {t('center.final.costs.instructions.fields.highlighted.in.red')}
            </Typography>
            <Typography variant={'secondary-body'} color={tailwindColors.grey.lighter}>
              {t('center.final.costs.instructions.you.can.edit.fields')}
            </Typography>
            <Typography variant={'secondary-body'} color={tailwindColors.grey.lighter}>
              {t('center.final.costs.instructions.it.is.essential.to.upload.all.invoices')}
            </Typography>
          </div>
        </div>
      </Box>

      {(olPermissions['center-costs-local-organizers'] ||
        olPermissions['center-costs-host-family']) && (
        <div
          className={cn('flex flex-row gap-4', {
            'flex-col': productTypes.includes(ProductTypeEnum.ONE_TO_ONE)
          })}
        >
          {olPermissions['center-costs-local-organizers'] && (
            <InnerBox className={'w-full rounded-2xl'}>
              <CenterCosts.LocalOrganizers
                currency={currency}
                data={data}
                mode={mode['local-organizers']}
                costType={'final-cost'}
              />
            </InnerBox>
          )}

          {olPermissions['center-costs-host-family'] && (
            <InnerBox className={'w-full rounded-2xl'}>
              <CenterCosts.HostFamily
                currency={currency}
                data={data}
                mode={mode['host-family']}
                costType={'final-cost'}
              />
            </InnerBox>
          )}
        </div>
      )}

      {(olPermissions['center-costs-tefl'] || olPermissions['center-costs-teaching-hall']) && (
        <div
          className={cn('flex flex-row gap-4', {
            'flex-col': productTypes.includes(ProductTypeEnum.ONE_TO_ONE)
          })}
        >
          {olPermissions['center-costs-tefl'] && (
            <InnerBox className={'w-full rounded-2xl'}>
              <CenterCosts.Tefl
                currency={currency}
                data={data}
                mode={mode.tefl}
                costType={'final-cost'}
              />
            </InnerBox>
          )}

          {olPermissions['center-costs-teaching-hall'] && (
            <InnerBox className={'w-full rounded-2xl'}>
              <CenterCosts.TeachingHall
                currency={currency}
                data={data}
                mode={mode['teaching-hall']}
                costType={'final-cost'}
              />
            </InnerBox>
          )}
        </div>
      )}

      {olPermissions['center-costs-cultural-outings'] && (
        <InnerBox className={'w-full rounded-2xl'}>
          <CenterCosts.CulturalOutings
            currency={currency}
            data={data}
            mode={mode['cultural-outings']}
            costType={'final-cost'}
          />
        </InnerBox>
      )}

      {olPermissions['center-costs-arrival-departure-transfers'] && (
        <InnerBox className={'w-full rounded-2xl'}>
          <CenterCosts.ArrivalDepartureTransfers
            currency={currency}
            data={data}
            mode={mode['arrival-departure-transfers']}
            costType={'final-cost'}
          />
        </InnerBox>
      )}

      {olPermissions['center-costs-french-leader'] && (
        <InnerBox className={'w-full rounded-2xl'}>
          <CenterCosts.FrenchLeader
            id={id}
            currency={currency}
            costType={'final-cost'}
            mode={mode.budget}
          />
        </InnerBox>
      )}

      {olPermissions['center-costs-activities'] && (
        <InnerBox className={'w-full rounded-2xl'}>
          <CenterCosts.Activities
            currency={currency}
            data={data}
            costType={'final-cost'}
            mode={mode.activities}
          />
        </InnerBox>
      )}

      {olPermissions['center-costs-miscellaneous'] && (
        <InnerBox className={'w-full rounded-2xl'}>
          <CenterCosts.Miscellaneous
            currency={currency}
            data={data}
            mode={mode.miscellaneous}
            costType={'final-cost'}
          />
        </InnerBox>
      )}

      {olPermissions['center-costs-total-costs'] && (
        <InnerBox className={'w-full rounded-2xl bg-white-hover'}>
          <CenterCosts.FinalBalance data={data} refetch={props.refetch} currency={currency} />
        </InnerBox>
      )}

      <InnerBox className={'flex flex-row gap-2 items-center'}>
        {user.role === RoleEnum.Ol && finalCostsStatus === CenterStatus.OPEN && (
          <Checkbox checked={isConfirmed} onChange={() => setIsConfirmed(!isConfirmed)} />
        )}

        <Typography variant={'secondary-body'} color={labelColor[user.role]}>
          {t('center.costs.final.balance.confirmational.message')}
        </Typography>

        {paymentConfirmationCheckedAt && (
          <Badge
            variant={'grey'}
            text={`${t('center.costs.final.balance.signed.on')} ${DateFormatter.DDMMYYYY(paymentConfirmationCheckedAt)}`}
          />
        )}
      </InnerBox>

      <CenterCosts.Status
        data={data}
        costType={'final-cost'}
        finalTransferCost={finalCostVisibility}
        disabled={user.role === RoleEnum.Ol && !isConfirmed}
        refetch={async () => {
          await refetch?.()
        }}
      />
    </div>
  )
}
